import React from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import { isCommunityCCRC } from 'components/Community/utils';
import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import { LIVING_TYPES } from 'constants/livingTypes';
import { isNonNegativeNumber } from 'utils/helpers/numbers';
import { raiseUnderscore } from 'utils/helpers/string';
import { formatAreaByUnitSystem, getAreaUnitLabel } from 'utils/helpers/units';

import { ACCOMMODATION_FIELDS, FIELD_CATEGORY } from '../../../ShopData/constants';
import { useCareTypesContext } from '../CareTypesContext';
import ItemViewHistory from '../common/ItemViewHistory';
import { ITEM_VIEW_HISTORY_CLASS } from '../constants';
import { LivingInfoDataType } from '../types';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
  padding: 6px 20px 6px 45px;
  border-bottom: solid 1px var(--line-gray);
  box-sizing: border-box;
  gap: 20px;
  &:hover ${`.${ITEM_VIEW_HISTORY_CLASS}`} {
    opacity: 1;
    visibility: visible;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  text-wrap: auto;
  gap: 4px;
`;

const Content = styled.div`
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 35%;
  flex-shrink: 0;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  gap: 6px;
`;

const UnitTypeTitle = styled.span`
  padding-left: 2px;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const Area = styled.div`
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--text-secondary);
`;

const Occupancy = styled.div`
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--text-secondary);
`;

const Values = styled.div`
  display: flex;
  flex-direction: column;
`;

const RentAmount = styled.div`
  display: flex;
  align-items: center;
`;

const EntranceFeeAmount = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  gap: 2px;
  color: var(--text-secondary);
`;

const Price = styled.div`
  font-family: var(--font-regular);
  font-size: 14px;
  color: var(--positive-green);
`;

const Frequency = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

type Props = {
  item: LivingInfoDataType;
};

const AccommodationGroupCollapseItem = ({ item }: Props) => {
  const { setHistory, communityInfo, unitSource } = useCommunityInfoContext();
  const { contract } = useCareTypesContext();
  const isCcrc = isCommunityCCRC(communityInfo?.rental_types || []);
  const isSkilledLiving = item.living_type_id === LIVING_TYPES.SKILLED_NURSING;
  const isIndependentLiving = item.living_type_id === LIVING_TYPES.INDEPENDENT_LIVING;
  const areaUnitLabel = getAreaUnitLabel(unitSource);
  const dataTestKey = `cd-accommodation-room-type-variant-${raiseUnderscore(
    item.apartment_type ?? 'unknown'
  ).toLowerCase()}`;

  return (
    <Container data-test-id={dataTestKey}>
      <Content>
        <TitleContainer>
          <Title>
            <span>{item.apartment_type}</span>

            {item.unit_type && (
              <>
                <span>·</span>
                <UnitTypeTitle>{item.unit_type.name}</UnitTypeTitle>
              </>
            )}
          </Title>
          <ItemViewHistory
            onClick={() =>
              setHistory({
                category: FIELD_CATEGORY.ACCOMMODATIONS,
                apartment_type: item.apartment_type_id,
                field: ACCOMMODATION_FIELDS.BASE_RENT,
                community_contract_id: contract?.id,
                label: item.apartment_type,
              })
            }
          />
        </TitleContainer>
        <ItemContent data-test-id={`${dataTestKey}-area`}>
          {item.sq_footage && (
            <Area>
              {formatAreaByUnitSystem(item.sq_footage, unitSource)} {areaUnitLabel}
            </Area>
          )}
          {isNonNegativeNumber(item.occupancy_percentage) && (
            <>
              <span>·</span>
              <Occupancy data-test-id={`${dataTestKey}-occupancy`}>{item.occupancy_percentage}% Occupied</Occupancy>
            </>
          )}
        </ItemContent>
      </Content>
      {item.notes && (
        <Typography.Paragraph
          data-test-id={`${dataTestKey}-note`}
          style={{ width: '100%', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 0 }}
          ellipsis={{
            rows: 2,
            expandable: 'collapsible',
            symbol: (expanded: boolean) => (
              <div style={{ color: 'var(--dark-blue)', fontSize: 12 }}>{expanded ? 'Show Less' : 'Show More'}</div>
            ),
          }}
        >
          {item.notes}
        </Typography.Paragraph>
      )}
      {isNonNegativeNumber(item.base_rent) && (
        <Values>
          <RentAmount data-test-id={`${dataTestKey}-value`}>
            <Price>${item.base_rent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Price>
            <Frequency>{isSkilledLiving ? '/day' : '/mo'}</Frequency>
          </RentAmount>
          {isCcrc && isIndependentLiving && isNonNegativeNumber(item.entrance_fee) && (
            <EntranceFeeAmount data-test-id={`${dataTestKey}-entrance-fee`}>
              <span style={{ color: 'var(--text-primary)' }}>
                ${(item.entrance_fee as number).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
              <span>ent. fee</span>
            </EntranceFeeAmount>
          )}
        </Values>
      )}
    </Container>
  );
};

export default AccommodationGroupCollapseItem;
